import moment from 'moment';
import { language, t } from 'utils/i18n';

import type {
    FrameStatus, StepCounts, StepFailure, StepState, StepStatus, StepsScope, StepsState,
} from './steps-api';

export type Segment = 'done' | 'running' | 'failed' | 'pending';
export type Target = 'all' | 'current' | 'selected';

export function countedSteps(steps: StepState[]): StepState[] {
    // aligning has a screen of its own: it is no part of a run or of the progress
    return steps.filter((step) => step.available && step.enabled && step.kind !== 'align');
}

export function summary(steps: StepState[]): { done: number; total: number } {
    const counted = countedSteps(steps);
    return { done: counted.filter((step) => step.status === 'done').length, total: counted.length };
}

export function segments(steps: StepState[]): Segment[] {
    return countedSteps(steps).map((step) => {
        if (step.status === 'done') return 'done';
        if (step.status === 'running' || step.status === 'queued') return 'running';
        if (step.status === 'failed') return 'failed';
        return 'pending';
    });
}

export function remainingStepIds(steps: StepState[]): string[] {
    return countedSteps(steps).filter((step) => step.status !== 'done').map((step) => step.id);
}

// mirrors the server: a step runs where what it builds on is done, or is part of the same run
export function blockedBy(
    step: StepState, steps: StepState[], frames: number[], runIds: string[],
): string | null {
    const outside = step.requires.filter((id) => !runIds.includes(id));
    if (!outside.length) return null;
    const required = outside.map((id) => steps.find((item) => item.id === id));
    const ready = frames.some((frame) => required.every((item) => item?.done_frames?.includes(frame)));
    if (ready) return null;
    const names = required.map((item, index) => `“${t(item?.title ?? outside[index])}”`).join(t(' and '));
    return t('Run {names} first', { names });
}

export function runnableRemaining(steps: StepState[], frames: number[]): string[] {
    const remaining = remainingStepIds(steps);
    return remaining.filter((id) => {
        const step = steps.find((item) => item.id === id) as StepState;
        return blockedBy(step, steps, frames, remaining) === null;
    });
}

// the drawings keep their key from before references had steps
const TARGET_KEY = (taskId: number, scope: StepsScope): string => (
    `cvat-steps-target-${taskId}${scope === 'references' ? '-references' : ''}`
);
const TARGETS: Target[] = ['all', 'current', 'selected'];

// the open drawing by default; the user's choice is kept while the browser tab lives
export function storedTarget(taskId: number, scope: StepsScope = 'drawings'): Target {
    try {
        const value = window.sessionStorage.getItem(TARGET_KEY(taskId, scope)) as Target | null;
        return value && TARGETS.includes(value) ? value : 'current';
    } catch {
        return 'current';
    }
}

export function storeTarget(taskId: number, target: Target, scope: StepsScope = 'drawings'): void {
    try {
        window.sessionStorage.setItem(TARGET_KEY(taskId, scope), target);
    } catch {
        // storage blocked: the choice lasts until the panel remounts
    }
}

export function targetFrames(target: Target, current: number, selected: number[]): number[] | null {
    if (target === 'current') return [current];
    if (target === 'selected') return [...selected].sort((a, b) => a - b);
    return null;
}

export function targetLabel(
    target: Target, total: number, selectedCount: number, noun: 'drawing' | 'photo' = 'drawing',
): string {
    if (target === 'current') return noun === 'photo' ? t('on this photo') : t('on this drawing');
    if (target === 'selected') return t('on {n} selected', { n: selectedCount });
    return t('on all {n}', { n: total });
}

export function rowAction(step: StepState): { label: 'Run' | 'Run again'; force: boolean } | null {
    if (!step.available || !step.enabled || step.kind === 'align') return null;
    if (step.status === 'running' || step.status === 'queued') return null;
    if (step.status === 'done') return { label: 'Run again', force: true };
    // only the failed and missing drawings run again; the done ones keep their results
    if (step.status === 'failed') return { label: 'Run again', force: false };
    return { label: 'Run', force: false };
}

// a failed step's Run again must reach its failures even when the chosen drawings are all done
export function rowFrames(step: StepState, frames: number[] | null): number[] | null {
    if (step.status !== 'failed' || frames === null) return frames;
    return frames.some((frame) => !step.done_frames?.includes(frame)) ? frames : null;
}

// mirrors the server's step status, for the counts of some of the drawings
function statusOf(counts: StepCounts): StepStatus {
    if (counts.running) return 'running';
    if (counts.queued) return 'queued';
    if (counts.failed) return 'failed';
    if (counts.total && counts.done === counts.total) return 'done';
    if (counts.done) return 'partial';
    return 'pending';
}

// the step as it stands on the given drawings only: what happened on the others is not shown
export function scopeStep(step: StepState, frames: number[]): StepState {
    // aligning is done for the task as a whole, not drawing by drawing
    if (!step.available || !step.enabled || step.kind === 'align') return step;
    const scope = new Set(frames);
    const count = (list?: number[]): number => (list ?? []).filter((frame) => scope.has(frame)).length;
    const failures = (step.failures ?? []).filter((failure) => scope.has(failure.frame));
    const counts = {
        done: count(step.done_frames),
        failed: failures.length,
        running: count(step.running_frames),
        queued: count(step.queued_frames),
        total: frames.length,
    };
    const latest = failures[0];
    return {
        ...step,
        counts,
        status: statusOf(counts),
        failures,
        error: latest?.error ?? null,
        error_detail: latest?.error_detail ?? null,
    };
}

export function failedFrames(step: StepState): number[] {
    return (step.failures ?? []).map((failure) => failure.frame).sort((a, b) => a - b);
}

export function failureAge(failure: StepFailure, now: Date = new Date()): string {
    return t('failed {ago}', { ago: moment(failure.at).locale(language).from(now) });
}

// what a failure tooltip says: the technical text, if any, and how old the failure is
export function failureTooltip(step: StepState, now: Date = new Date()): string {
    const latest = step.failures?.[0];
    const lines = [step.error_detail || step.error || ''];
    if (latest) lines.push(failureAge(latest, now));
    return lines.filter(Boolean).join('\n');
}

// one drawing has no counter: just what the step did there
export function progressPercent(step: StepState): number {
    const { done, total } = step.counts;
    return total ? Math.round((done / total) * 100) : 0;
}

export function countLabel(step: StepState): string {
    if (!step.available || step.kind === 'align') return '';
    if (!step.enabled) return t('off');
    // the failures have a line of their own, with the error and Retry failed
    const { done, total } = step.counts;
    return `${done}/${total}`;
}

export function frameStatusMap(state: StepsState | null): Record<number, FrameStatus> {
    if (!state) return {};
    return Object.keys(state.frame_status).reduce((acc, key) => {
        acc[Number(key)] = state.frame_status[key];
        return acc;
    }, {} as Record<number, FrameStatus>);
}

// the Steps API answers in English: the known messages are shown in the UI language, the rest as they come
export function localizeStepsMessage(message: string): string {
    const requirement = message.match(/^Run (“.+”) first$/);
    if (requirement) {
        const names = requirement[1].split('” and “').map((name) => name.replace(/[“”]/g, ''));
        return t('Run {names} first', { names: names.map((name) => `“${t(name)}”`).join(t(' and ')) });
    }
    const switchedOff = message.match(/^(.+) is switched off for this task$/);
    if (switchedOff) return t('{title} is switched off for this task', { title: t(switchedOff[1]) });
    const unavailable = message.match(/^([^:]+): (.+)$/);
    if (unavailable && !message.startsWith('No such')) return `${t(unavailable[1])}: ${t(unavailable[2])}`;
    return t(message);
}
